import { Divider, Stack } from '@mui/material';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import useViewportFocus from '~/hooks/useViewportFocus';
import {
    autoShownRoiIdState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import StructureSet from '../../ContouringStep/StructureSet/StructureSet';
import ReviewControls from './ReviewControls';
import ReviewPlayer from './ReviewPlayer';

/**
 * Each structure's own visibility: the structure `selection` shows only
 * because it is selected counts as hidden.
 */
function ownVisibilityOf(
    rois: readonly RoiReadModel[],
    selection: { roiId: RoiId | null; autoShownRoiId: RoiId | null }
): Map<RoiId, boolean> {
    return new Map(
        rois.map((r) => [
            r.roiId,
            r.visible &&
                !(
                    r.roiId === selection.roiId &&
                    r.roiId === selection.autoShownRoiId
                ),
        ])
    );
}

export default function ReviewStructureSet({
    structureSet,
}: {
    structureSet: StructureSetReadModel;
}) {
    const selectedRoiId = useRecoilValue(selectedROIState);
    const { focusOnStructure } = useViewportFocus();
    const readSelection = useRecoilCallback(
        ({ snapshot }) =>
            () => ({
                roiId: snapshot.getLoadable(selectedROIState).getValue(),
                autoShownRoiId: snapshot
                    .getLoadable(autoShownRoiIdState)
                    .getValue(),
            }),
        []
    );
    const setAutoShown = useSetRecoilState(autoShownRoiIdState);
    const shownSetUidRef = useRef(structureSet.dicomUid);
    shownSetUidRef.current = structureSet.dicomUid;
    const ownVisibilityBySetRef = useRef(
        new Map<string, Map<RoiId, boolean>>()
    );

    // Review owns visibility while mounted. The first time it shows a
    // structure set it records each structure's own visibility, and reuses
    // that record when it shows the set again, by when the set carries
    // Review's hiding. On unmount it restores the record of the set shown
    // and re-applies the selection rule to the structure selected now:
    // shown, and marked auto-shown only if its own visibility is hidden, so
    // a later deselect cannot hide a structure the user had shown.
    // Visibility requests and the selection belong to the current set, so a
    // record is applied only while its own set is the one shown.
    useEffect(() => {
        const recorded = ownVisibilityBySetRef.current;
        const ownVisibility =
            recorded.get(structureSet.dicomUid) ??
            ownVisibilityOf(structureSet.roiArray, readSelection());
        recorded.set(structureSet.dicomUid, ownVisibility);
        return () => {
            if (shownSetUidRef.current !== structureSet.dicomUid) {
                return;
            }
            const selected = readSelection().roiId;
            ownVisibility.forEach((visible, roiId) => {
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roiId,
                        visible || roiId === selected,
                        'user'
                    )
                );
            });
            setAutoShown(
                selected !== null && ownVisibility.get(selected) === false
                    ? selected
                    : null
            );
        };
        // structureSet.roiArray intentionally omitted. The snapshot is
        // taken once per structure set, not on every visibility update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structureSet.dicomUid]);

    // When the selected ROI changes, hide all other ROIs, make the selected
    // one visible, and auto-focus the viewport.
    useEffect(() => {
        if (selectedRoiId === null) {
            return;
        }
        structureSet.roiArray.forEach((roi) => {
            domainEventBus.publish(
                new RoiVisibilityChangeRequestedEvent(
                    roi.roiId,
                    roi.roiId === selectedRoiId,
                    'user'
                )
            );
        });
        focusOnStructure(selectedRoiId);
        // focusOnStructure intentionally omitted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoiId, structureSet.dicomUid]);

    const renderReviewExtras = (roi: RoiReadModel) => (
        <Stack spacing={1.5} mt={2}>
            <Divider />
            <ReviewPlayer roi={roi} showLabel={false} />
            <ReviewControls roi={roi} />
        </Stack>
    );

    return (
        // The Review step reviews and approves what contouring produced; it is
        // not an authoring surface. readOnly keeps ctrl-click multi-select (and
        // its Copy) available while withholding structure creation and the bulk
        // Delete, which was otherwise reachable here on an unapproved set.
        <StructureSet
            structureSet={structureSet}
            renderExtraDetails={renderReviewExtras}
            readOnly
            selectHint={
                <FormattedMessage id="workflow.review.selectStructureToStart" />
            }
        />
    );
}
